import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  FileBarChart, TrendingUp, TrendingDown, Users, Truck, Printer, 
  Wallet, Building2, CreditCard, ArrowUpCircle, ArrowDownCircle, 
  Scale, CircleDollarSign, Banknote, Download, RefreshCw, Calendar,
  Package, Wrench, Receipt, PieChart, BarChart3, LineChart, Filter,
  ChevronDown, ChevronUp, FileText, DollarSign, Percent, Target
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";



const formatCurrency = (value) => {
  return `₺${(value || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("tr-TR");
};

// Summary Card Component
const SummaryCard = ({ title, value, icon: Icon, color = "blue", trend, trendValue, subtitle }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    green: "bg-emerald-50 text-emerald-600 border-emerald-200",
    red: "bg-red-50 text-red-600 border-red-200",
    amber: "bg-amber-50 text-amber-600 border-amber-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200",
  };

  return (
    <Card className={`border-2 ${colors[color].split(' ')[2]}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs text-slate-500">{title}</p>
            <p className="text-xl font-bold text-slate-900 tabular-nums">{formatCurrency(value)}</p>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center gap-1 text-xs ${trend === "up" ? "text-emerald-600" : "text-red-600"}`}>
                {trend === "up" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`p-2 rounded-lg ${colors[color].split(' ').slice(0, 2).join(' ')}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Period Selector Component
const PeriodSelector = ({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-40">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="today">Bugün</SelectItem>
      <SelectItem value="week">Bu Hafta</SelectItem>
      <SelectItem value="month">Bu Ay</SelectItem>
      <SelectItem value="quarter">Bu Çeyrek</SelectItem>
      <SelectItem value="year">Bu Yıl</SelectItem>
      <SelectItem value="all">Tümü</SelectItem>
      <SelectItem value="custom">Özel Tarih</SelectItem>
    </SelectContent>
  </Select>
);

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [debtDetail, setDebtDetail] = useState(null);
  const [incomeExpense, setIncomeExpense] = useState(null);
  const [customerBalance, setCustomerBalance] = useState(null);
  const [supplierBalance, setSupplierBalance] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [salesReport, setSalesReport] = useState(null);
  const [purchasesReport, setPurchasesReport] = useState(null);
  const [expensesReport, setExpensesReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Filter states
  const [period, setPeriod] = useState("month");
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0],
    end_date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchAllReports();
  }, []);

  useEffect(() => {
    if (period !== "custom") {
      updateDateRange(period);
    }
  }, [period]);

  const updateDateRange = (selectedPeriod) => {
    const now = new Date();
    let start, end = now.toISOString().split("T")[0];
    
    switch (selectedPeriod) {
      case "today":
        start = end;
        break;
      case "week":
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        start = weekStart.toISOString().split("T")[0];
        break;
      case "month":
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
        break;
      case "quarter":
        const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
        start = new Date(now.getFullYear(), quarterMonth, 1).toISOString().split("T")[0];
        break;
      case "year":
        start = new Date(now.getFullYear(), 0, 1).toISOString().split("T")[0];
        break;
      case "all":
        start = "2020-01-01";
        break;
      default:
        return;
    }
    
    setDateRange({ start_date: start, end_date: end });
  };

  const fetchAllReports = async () => {
    setRefreshing(true);
    try {
      const params = `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
      
      const [summaryRes, debtDetailRes, custBalRes, suppBalRes, incExpRes, cashFlowRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/reports/summary`),
        axios.get(`${API}/reports/debt-receivable-detail`),
        axios.get(`${API}/reports/customer-balance`),
        axios.get(`${API}/reports/supplier-balance`),
        axios.get(`${API}/reports/income-expense${params}`),
        axios.get(`${API}/reports/cash-flow${params}`),
        axios.get(`${API}/reports/monthly-summary?year=${selectedYear}`),
      ]);
      
      setSummary(summaryRes.data);
      setDebtDetail(debtDetailRes.data);
      setCustomerBalance(custBalRes.data);
      setSupplierBalance(suppBalRes.data);
      setIncomeExpense(incExpRes.data);
      setCashFlow(cashFlowRes.data);
      setMonthlyReport(monthlyRes.data);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      toast.error("Raporlar yüklenemedi");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchMonthlyReport = async (year) => {
    try {
      const res = await axios.get(`${API}/reports/monthly-summary?year=${year}`);
      setMonthlyReport(res.data);
    } catch (error) {
      console.error("Failed to fetch monthly report:", error);
      toast.error("Aylık rapor yüklenemedi");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const exportToPDF = (reportType) => {
    const doc = new jsPDF();
    doc.setFont("helvetica");
    
    // Title
    doc.setFontSize(18);
    doc.text("Ön Muhasebe Raporu", 14, 20);
    doc.setFontSize(10);
    doc.text(`Tarih: ${formatDate(dateRange.start_date)} - ${formatDate(dateRange.end_date)}`, 14, 28);
    doc.text(`Oluşturulma: ${new Date().toLocaleString("tr-TR")}`, 14, 34);
    
    let yPos = 45;
    
    if (reportType === "overview" && debtDetail) {
      doc.setFontSize(14);
      doc.text("Genel Özet", 14, yPos);
      yPos += 10;
      
      const overviewData = [
        ["Toplam Alacak", formatCurrency(debtDetail.summary.total_receivables)],
        ["Toplam Borç", formatCurrency(debtDetail.summary.total_payables)],
        ["Nakit Varlık", formatCurrency(debtDetail.summary.total_liquid_assets)],
        ["Net Denge", formatCurrency(debtDetail.summary.net_balance)],
      ];
      
      doc.autoTable({
        startY: yPos,
        head: [["Açıklama", "Tutar"]],
        body: overviewData,
        theme: "grid",
      });
    }
    
    doc.save(`rapor_${reportType}_${dateRange.start_date}_${dateRange.end_date}.pdf`);
    toast.success("PDF indirildi");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const netBalance = debtDetail?.summary?.net_balance || 0;
  const isPositive = netBalance >= 0;

  return (
    <div className="space-y-6 print:space-y-4" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 no-print">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Raporlar
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">Detaylı finansal analiz ve raporlama</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          {period === "custom" && (
            <>
              <Input
                type="date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                className="w-36"
              />
              <span className="text-slate-400">-</span>
              <Input
                type="date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                className="w-36"
              />
            </>
          )}
          <Button onClick={fetchAllReports} disabled={refreshing} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
          <Button onClick={handlePrint} variant="outline">
            <Printer className="h-4 w-4 mr-2" />
            Yazdır
          </Button>
          <Button onClick={() => exportToPDF(activeTab)}>
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Net Balance Hero */}
      <Card className={`${isPositive ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-red-600'} text-white print:bg-slate-100 print:text-slate-900`}>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-white/20 print:bg-slate-200 flex items-center justify-center">
                <Scale className="h-8 w-8" />
              </div>
              <div>
                <p className="text-white/80 print:text-slate-600 text-sm">Güncel Borç-Alacak Dengesi</p>
                <p className="text-4xl font-bold tabular-nums">{formatCurrency(Math.abs(netBalance))}</p>
                <p className="text-white/80 print:text-slate-600 text-sm mt-1">
                  {isPositive ? '✓ Pozitif Denge' : '⚠ Negatif Denge'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-white/70 print:text-slate-500 text-xs">Müşteri Alacakları</p>
                <p className="text-xl font-bold tabular-nums">{formatCurrency(debtDetail?.summary?.total_receivables)}</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 print:text-slate-500 text-xs">Tedarikçi Borçları</p>
                <p className="text-xl font-bold tabular-nums">{formatCurrency(debtDetail?.summary?.total_payables)}</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 print:text-slate-500 text-xs">Nakit Varlıklar</p>
                <p className="text-xl font-bold tabular-nums">{formatCurrency(debtDetail?.summary?.total_liquid_assets)}</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 print:text-slate-500 text-xs">K.Kartı Borcu</p>
                <p className="text-xl font-bold tabular-nums">{formatCurrency(debtDetail?.accounts?.credit_cards?.total_debt)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <SummaryCard title="Toplam Satış" value={summary?.total_sales} icon={TrendingUp} color="green" />
        <SummaryCard title="Toplam Alış" value={summary?.total_purchases} icon={TrendingDown} color="red" />
        <SummaryCard title="Toplam Gider" value={summary?.total_expenses} icon={Receipt} color="amber" />
        <SummaryCard title="Net Kar" value={summary?.net_profit} icon={CircleDollarSign} color={summary?.net_profit >= 0 ? "green" : "red"} />
        <SummaryCard title="Tahsilatlar" value={summary?.total_collections} icon={ArrowDownCircle} color="blue" />
        <SummaryCard title="Ödemeler" value={summary?.total_payments} icon={ArrowUpCircle} color="purple" />
      </div>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="no-print flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
          <TabsTrigger value="overview" className="text-xs md:text-sm">
            <PieChart className="h-4 w-4 mr-1 hidden sm:inline" /> Genel Bakış
          </TabsTrigger>
          <TabsTrigger value="monthly" className="text-xs md:text-sm">
            <Calendar className="h-4 w-4 mr-1 hidden sm:inline" /> Aylık Rapor
          </TabsTrigger>
          <TabsTrigger value="income-expense" className="text-xs md:text-sm">
            <BarChart3 className="h-4 w-4 mr-1 hidden sm:inline" /> Gelir-Gider
          </TabsTrigger>
          <TabsTrigger value="cash-flow" className="text-xs md:text-sm">
            <LineChart className="h-4 w-4 mr-1 hidden sm:inline" /> Nakit Akış
          </TabsTrigger>
          <TabsTrigger value="customers" className="text-xs md:text-sm">
            <Users className="h-4 w-4 mr-1 hidden sm:inline" /> Müşteriler
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="text-xs md:text-sm">
            <Truck className="h-4 w-4 mr-1 hidden sm:inline" /> Tedarikçiler
          </TabsTrigger>
          <TabsTrigger value="accounts" className="text-xs md:text-sm">
            <Wallet className="h-4 w-4 mr-1 hidden sm:inline" /> Hesaplar
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Profit Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                Kar/Zarar Analizi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-sm text-emerald-600 mb-1">Toplam Gelir</p>
                  <p className="text-2xl font-bold text-emerald-700 tabular-nums">{formatCurrency(summary?.total_sales)}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-sm text-red-600 mb-1">Toplam Maliyet</p>
                  <p className="text-2xl font-bold text-red-700 tabular-nums">{formatCurrency(summary?.total_purchases)}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-sm text-amber-600 mb-1">Toplam Gider</p>
                  <p className="text-2xl font-bold text-amber-700 tabular-nums">{formatCurrency(summary?.total_expenses)}</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${summary?.net_profit >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
                  <p className={`text-sm mb-1 ${summary?.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>Net Kar/Zarar</p>
                  <p className={`text-2xl font-bold tabular-nums ${summary?.net_profit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                    {formatCurrency(summary?.net_profit)}
                  </p>
                </div>
              </div>
              
              {/* Profit Margin */}
              {summary?.total_sales > 0 && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Kar Marjı</span>
                    <span className="text-lg font-bold text-slate-900">
                      %{((summary?.net_profit / summary?.total_sales) * 100).toFixed(1)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${summary?.net_profit >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(Math.abs((summary?.net_profit / summary?.total_sales) * 100), 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Receivables & Payables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Customer Receivables */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ArrowDownCircle className="h-5 w-5 text-emerald-600" />
                    Müşteri Alacakları
                  </CardTitle>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{debtDetail?.customers?.receivables?.count || 0} müşteri</p>
                    <p className="text-lg font-bold text-emerald-600 tabular-nums">
                      {formatCurrency(debtDetail?.customers?.receivables?.total)}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 max-h-72 overflow-y-auto">
                {debtDetail?.customers?.receivables?.list?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Müşteri</TableHead>
                        <TableHead className="text-xs text-right">Alacak</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {debtDetail.customers.receivables.list.map((customer, idx) => (
                        <TableRow key={customer.id || idx}>
                          <TableCell className="py-2">
                            <p className="font-medium text-sm">{customer.name}</p>
                            {customer.phone && <p className="text-xs text-slate-500">{customer.phone}</p>}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-600 tabular-nums text-sm py-2">
                            {formatCurrency(customer.balance)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500 text-sm">Alacak bulunmuyor</div>
                )}
              </CardContent>
            </Card>

            {/* Supplier Debts */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ArrowUpCircle className="h-5 w-5 text-red-600" />
                    Tedarikçi Borçları
                  </CardTitle>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{debtDetail?.suppliers?.debts?.count || 0} tedarikçi</p>
                    <p className="text-lg font-bold text-red-600 tabular-nums">
                      {formatCurrency(debtDetail?.suppliers?.debts?.total)}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 max-h-72 overflow-y-auto">
                {debtDetail?.suppliers?.debts?.list?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Tedarikçi</TableHead>
                        <TableHead className="text-xs text-right">Borç</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {debtDetail.suppliers.debts.list.map((supplier, idx) => (
                        <TableRow key={supplier.id || idx}>
                          <TableCell className="py-2">
                            <p className="font-medium text-sm">{supplier.name}</p>
                            {supplier.phone && <p className="text-xs text-slate-500">{supplier.phone}</p>}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600 tabular-nums text-sm py-2">
                            {formatCurrency(supplier.balance)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500 text-sm">Borç bulunmuyor</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Account Balances Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="h-5 w-5 text-blue-600" />
                Hesap Bakiyeleri Özeti
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Cash */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Banknote className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Kasa Hesapları</p>
                      <p className="text-xl font-bold text-green-600 tabular-nums">
                        {formatCurrency(debtDetail?.accounts?.cash?.total)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {debtDetail?.accounts?.cash?.list?.map((acc, idx) => (
                      <div key={acc.id || idx} className="flex justify-between text-sm">
                        <span className="text-slate-600">{acc.name}</span>
                        <span className="font-medium tabular-nums">{formatCurrency(acc.balance)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bank */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Banka Hesapları</p>
                      <p className="text-xl font-bold text-blue-600 tabular-nums">
                        {formatCurrency(debtDetail?.accounts?.bank?.total)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {debtDetail?.accounts?.bank?.list?.map((acc, idx) => (
                      <div key={acc.id || idx} className="flex justify-between text-sm">
                        <span className="text-slate-600 truncate mr-2">{acc.name}</span>
                        <span className="font-medium tabular-nums">{formatCurrency(acc.balance)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Credit Cards */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <CreditCard className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Kredi Kartları</p>
                      <p className="text-xl font-bold text-red-600 tabular-nums">
                        -{formatCurrency(debtDetail?.accounts?.credit_cards?.total_debt)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {debtDetail?.accounts?.credit_cards?.list?.map((card, idx) => (
                      <div key={card.id || idx} className="flex justify-between text-sm">
                        <span className="text-slate-600 truncate mr-2">{card.name}</span>
                        <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(card.current_debt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Report Tab */}
        <TabsContent value="monthly" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-indigo-600" />
                    {selectedYear} Yılı Aylık Raporu
                  </CardTitle>
                  <CardDescription>
                    Yılbaşından itibaren aylara göre satış, alış ve gider özeti
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select 
                    value={selectedYear.toString()} 
                    onValueChange={(val) => {
                      setSelectedYear(parseInt(val));
                      fetchMonthlyReport(parseInt(val));
                    }}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {monthlyReport ? (
                <div className="space-y-6">
                  {/* Yıllık Toplamlar */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-xs text-emerald-600 font-medium">Toplam Satış</p>
                      <p className="text-xl font-bold text-emerald-700 tabular-nums">
                        {formatCurrency(monthlyReport.totals.sales_total)}
                      </p>
                      <p className="text-xs text-emerald-600">{monthlyReport.totals.sales_count} adet</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <p className="text-xs text-red-600 font-medium">Toplam Alış</p>
                      <p className="text-xl font-bold text-red-700 tabular-nums">
                        {formatCurrency(monthlyReport.totals.purchases_total)}
                      </p>
                      <p className="text-xs text-red-600">{monthlyReport.totals.purchases_count} adet</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <p className="text-xs text-amber-600 font-medium">Toplam Gider</p>
                      <p className="text-xl font-bold text-amber-700 tabular-nums">
                        {formatCurrency(monthlyReport.totals.expenses_total)}
                      </p>
                      <p className="text-xs text-amber-600">{monthlyReport.totals.expenses_count} adet</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-xs text-blue-600 font-medium">Toplam Tahsilat</p>
                      <p className="text-xl font-bold text-blue-700 tabular-nums">
                        {formatCurrency(monthlyReport.totals.collections_total)}
                      </p>
                      <p className="text-xs text-blue-600">{monthlyReport.totals.collections_count} adet</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-xs text-purple-600 font-medium">Toplam Ödeme</p>
                      <p className="text-xl font-bold text-purple-700 tabular-nums">
                        {formatCurrency(monthlyReport.totals.payments_total)}
                      </p>
                      <p className="text-xs text-purple-600">{monthlyReport.totals.payments_count} adet</p>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${monthlyReport.totals.net_profit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className={`text-xs font-medium ${monthlyReport.totals.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>Net Kar/Zarar</p>
                      <p className={`text-xl font-bold tabular-nums ${monthlyReport.totals.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(monthlyReport.totals.net_profit)}
                      </p>
                    </div>
                  </div>

                  {/* Aylık Detay Tablosu */}
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-100">
                          <TableHead className="font-semibold">Ay</TableHead>
                          <TableHead className="text-right font-semibold">Satış</TableHead>
                          <TableHead className="text-right font-semibold">Adet</TableHead>
                          <TableHead className="text-right font-semibold">Alış</TableHead>
                          <TableHead className="text-right font-semibold">Adet</TableHead>
                          <TableHead className="text-right font-semibold">Gider</TableHead>
                          <TableHead className="text-right font-semibold">Adet</TableHead>
                          <TableHead className="text-right font-semibold">Tahsilat</TableHead>
                          <TableHead className="text-right font-semibold">Ödeme</TableHead>
                          <TableHead className="text-right font-semibold">Brüt Kar</TableHead>
                          <TableHead className="text-right font-semibold">Net Kar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {monthlyReport.months.map((month) => (
                          <TableRow key={month.month} className={month.sales.total > 0 || month.purchases.total > 0 ? '' : 'opacity-50'}>
                            <TableCell className="font-medium">{month.month_name}</TableCell>
                            <TableCell className="text-right text-emerald-600 font-semibold tabular-nums">
                              {formatCurrency(month.sales.total)}
                            </TableCell>
                            <TableCell className="text-right text-emerald-600 tabular-nums">
                              {month.sales.count}
                            </TableCell>
                            <TableCell className="text-right text-red-600 font-semibold tabular-nums">
                              {formatCurrency(month.purchases.total)}
                            </TableCell>
                            <TableCell className="text-right text-red-600 tabular-nums">
                              {month.purchases.count}
                            </TableCell>
                            <TableCell className="text-right text-amber-600 font-semibold tabular-nums">
                              {formatCurrency(month.expenses.total)}
                            </TableCell>
                            <TableCell className="text-right text-amber-600 tabular-nums">
                              {month.expenses.count}
                            </TableCell>
                            <TableCell className="text-right text-blue-600 tabular-nums">
                              {formatCurrency(month.collections.total)}
                            </TableCell>
                            <TableCell className="text-right text-purple-600 tabular-nums">
                              {formatCurrency(month.payments.total)}
                            </TableCell>
                            <TableCell className={`text-right font-semibold tabular-nums ${month.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(month.gross_profit)}
                            </TableCell>
                            <TableCell className={`text-right font-bold tabular-nums ${month.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {formatCurrency(month.net_profit)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-slate-100 font-bold">
                          <TableCell>TOPLAM</TableCell>
                          <TableCell className="text-right text-emerald-700 tabular-nums">
                            {formatCurrency(monthlyReport.totals.sales_total)}
                          </TableCell>
                          <TableCell className="text-right text-emerald-700 tabular-nums">
                            {monthlyReport.totals.sales_count}
                          </TableCell>
                          <TableCell className="text-right text-red-700 tabular-nums">
                            {formatCurrency(monthlyReport.totals.purchases_total)}
                          </TableCell>
                          <TableCell className="text-right text-red-700 tabular-nums">
                            {monthlyReport.totals.purchases_count}
                          </TableCell>
                          <TableCell className="text-right text-amber-700 tabular-nums">
                            {formatCurrency(monthlyReport.totals.expenses_total)}
                          </TableCell>
                          <TableCell className="text-right text-amber-700 tabular-nums">
                            {monthlyReport.totals.expenses_count}
                          </TableCell>
                          <TableCell className="text-right text-blue-700 tabular-nums">
                            {formatCurrency(monthlyReport.totals.collections_total)}
                          </TableCell>
                          <TableCell className="text-right text-purple-700 tabular-nums">
                            {formatCurrency(monthlyReport.totals.payments_total)}
                          </TableCell>
                          <TableCell className={`text-right tabular-nums ${monthlyReport.totals.gross_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatCurrency(monthlyReport.totals.gross_profit)}
                          </TableCell>
                          <TableCell className={`text-right tabular-nums ${monthlyReport.totals.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatCurrency(monthlyReport.totals.net_profit)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aylık rapor yükleniyor...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Income-Expense Tab */}
        <TabsContent value="income-expense" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Gelir-Gider Detayı</CardTitle>
              <CardDescription>
                {formatDate(dateRange.start_date)} - {formatDate(dateRange.end_date)} arası
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-600">Satış Gelirleri</p>
                  <p className="text-2xl font-bold text-emerald-700 tabular-nums">
                    {formatCurrency(incomeExpense?.totals?.sales)}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">{incomeExpense?.sales?.length || 0} adet satış</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600">Mal Alışları</p>
                  <p className="text-2xl font-bold text-red-700 tabular-nums">
                    {formatCurrency(incomeExpense?.purchases?.reduce((sum, p) => sum + (p.total || 0), 0))}
                  </p>
                  <p className="text-xs text-red-600 mt-1">{incomeExpense?.purchases?.length || 0} adet alış</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-600">Hizmet Alışları</p>
                  <p className="text-2xl font-bold text-orange-700 tabular-nums">
                    {formatCurrency(incomeExpense?.service_purchases?.reduce((sum, sp) => sum + (sp.amount || 0), 0))}
                  </p>
                  <p className="text-xs text-orange-600 mt-1">{incomeExpense?.service_purchases?.length || 0} adet hizmet</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-600">Giderler</p>
                  <p className="text-2xl font-bold text-amber-700 tabular-nums">
                    {formatCurrency(incomeExpense?.totals?.expenses)}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">{incomeExpense?.expenses?.length || 0} adet gider</p>
                </div>
                <div className={`p-4 rounded-lg ${(incomeExpense?.totals?.sales - incomeExpense?.totals?.purchases - incomeExpense?.totals?.expenses) >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
                  <p className="text-sm text-slate-600">Dönem Karı</p>
                  <p className={`text-2xl font-bold tabular-nums ${(incomeExpense?.totals?.sales - incomeExpense?.totals?.purchases - incomeExpense?.totals?.expenses) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                    {formatCurrency((incomeExpense?.totals?.sales || 0) - (incomeExpense?.totals?.purchases || 0) - (incomeExpense?.totals?.expenses || 0))}
                  </p>
                </div>
              </div>

              {/* Sales Table */}
              {incomeExpense?.sales?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-600" />
                    Satışlar ({incomeExpense.sales.length})
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Tarih</TableHead>
                          <TableHead className="text-xs">Fatura No</TableHead>
                          <TableHead className="text-xs">Müşteri</TableHead>
                          <TableHead className="text-xs text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {incomeExpense.sales.map((sale, idx) => (
                          <TableRow key={sale.id || idx}>
                            <TableCell className="text-sm">{formatDate(sale.date)}</TableCell>
                            <TableCell className="font-mono text-xs">{sale.invoice_no}</TableCell>
                            <TableCell className="text-sm">{sale.customer_name}</TableCell>
                            <TableCell className="text-right text-emerald-600 font-semibold tabular-nums">
                              +{formatCurrency(sale.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-emerald-50">
                          <TableCell colSpan={3} className="font-semibold">Toplam</TableCell>
                          <TableCell className="text-right font-bold text-emerald-700 tabular-nums">
                            {formatCurrency(incomeExpense.totals.sales)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              )}

              {/* Purchases Table */}
              {incomeExpense?.purchases?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4 text-red-600" />
                    Mal Alışları ({incomeExpense.purchases.length})
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Tarih</TableHead>
                          <TableHead className="text-xs">Fatura No</TableHead>
                          <TableHead className="text-xs">Tedarikçi</TableHead>
                          <TableHead className="text-xs text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {incomeExpense.purchases.map((purchase, idx) => (
                          <TableRow key={purchase.id || idx}>
                            <TableCell className="text-sm">{formatDate(purchase.date)}</TableCell>
                            <TableCell className="font-mono text-xs">{purchase.invoice_no}</TableCell>
                            <TableCell className="text-sm">{purchase.supplier_name}</TableCell>
                            <TableCell className="text-right text-red-600 font-semibold tabular-nums">
                              -{formatCurrency(purchase.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-red-50">
                          <TableCell colSpan={3} className="font-semibold">Toplam</TableCell>
                          <TableCell className="text-right font-bold text-red-700 tabular-nums">
                            -{formatCurrency(incomeExpense.purchases.reduce((sum, p) => sum + (p.total || 0), 0))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              )}

              {/* Service Purchases Table */}
              {incomeExpense?.service_purchases?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-orange-600" />
                    Hizmet Alışları ({incomeExpense.service_purchases.length})
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Tarih</TableHead>
                          <TableHead className="text-xs">Fatura No</TableHead>
                          <TableHead className="text-xs">Tedarikçi</TableHead>
                          <TableHead className="text-xs">Açıklama</TableHead>
                          <TableHead className="text-xs text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {incomeExpense.service_purchases.map((sp, idx) => (
                          <TableRow key={sp.id || idx}>
                            <TableCell className="text-sm">{formatDate(sp.date)}</TableCell>
                            <TableCell className="font-mono text-xs">{sp.invoice_no}</TableCell>
                            <TableCell className="text-sm">{sp.supplier_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{sp.description}</TableCell>
                            <TableCell className="text-right text-orange-600 font-semibold tabular-nums">
                              -{formatCurrency(sp.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-orange-50">
                          <TableCell colSpan={4} className="font-semibold">Toplam</TableCell>
                          <TableCell className="text-right font-bold text-orange-700 tabular-nums">
                            -{formatCurrency(incomeExpense.service_purchases.reduce((sum, sp) => sum + (sp.amount || 0), 0))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              )}

              {/* Expenses Table */}
              {incomeExpense?.expenses?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-amber-600" />
                    Giderler ({incomeExpense.expenses.length})
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Tarih</TableHead>
                          <TableHead className="text-xs">Kategori</TableHead>
                          <TableHead className="text-xs">Açıklama</TableHead>
                          <TableHead className="text-xs text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {incomeExpense.expenses.map((expense, idx) => (
                          <TableRow key={expense.id || idx}>
                            <TableCell className="text-sm">{formatDate(expense.date)}</TableCell>
                            <TableCell className="text-sm">{expense.category}</TableCell>
                            <TableCell className="text-sm text-slate-600">{expense.description}</TableCell>
                            <TableCell className="text-right text-amber-600 font-semibold tabular-nums">
                              -{formatCurrency(expense.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-amber-50">
                          <TableCell colSpan={3} className="font-semibold">Toplam</TableCell>
                          <TableCell className="text-right font-bold text-amber-700 tabular-nums">
                            -{formatCurrency(incomeExpense.totals.expenses)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              )}

              {!incomeExpense?.sales?.length && !incomeExpense?.purchases?.length && !incomeExpense?.service_purchases?.length && !incomeExpense?.expenses?.length && (
                <div className="text-center py-12">
                  <FileBarChart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Bu dönemde işlem bulunmuyor</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cash-flow" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Nakit Akış Raporu</CardTitle>
              <CardDescription>
                {formatDate(dateRange.start_date)} - {formatDate(dateRange.end_date)} arası
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-600">Nakit Girişi</p>
                  <p className="text-2xl font-bold text-emerald-700 tabular-nums">
                    +{formatCurrency(cashFlow?.summary?.inflows)}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">{cashFlow?.collections?.length || 0} tahsilat</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600">Nakit Çıkışı</p>
                  <p className="text-2xl font-bold text-red-700 tabular-nums">
                    -{formatCurrency(cashFlow?.summary?.outflows)}
                  </p>
                  <p className="text-xs text-red-600 mt-1">{(cashFlow?.payments?.length || 0) + (cashFlow?.expenses?.length || 0)} ödeme/gider</p>
                </div>
                <div className={`p-4 rounded-lg ${cashFlow?.summary?.net_flow >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}>
                  <p className="text-sm text-slate-600">Net Nakit Akışı</p>
                  <p className={`text-2xl font-bold tabular-nums ${cashFlow?.summary?.net_flow >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                    {formatCurrency(cashFlow?.summary?.net_flow)}
                  </p>
                </div>
              </div>

              {/* Collections */}
              {cashFlow?.collections?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <ArrowDownCircle className="h-4 w-4 text-emerald-600" />
                    Tahsilatlar ({cashFlow.collections.length})
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Tarih</TableHead>
                          <TableHead className="text-xs">Makbuz No</TableHead>
                          <TableHead className="text-xs">Müşteri</TableHead>
                          <TableHead className="text-xs">Yöntem</TableHead>
                          <TableHead className="text-xs text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cashFlow.collections.map((coll, idx) => (
                          <TableRow key={coll.id || idx}>
                            <TableCell className="text-sm">{formatDate(coll.date)}</TableCell>
                            <TableCell className="font-mono text-xs">{coll.receipt_no}</TableCell>
                            <TableCell className="text-sm">{coll.customer_name}</TableCell>
                            <TableCell className="text-sm">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                coll.method === "cash" ? "bg-green-100 text-green-700" :
                                coll.method === "bank" ? "bg-blue-100 text-blue-700" :
                                "bg-purple-100 text-purple-700"
                              }`}>
                                {coll.method === "cash" ? "Nakit" : coll.method === "bank" ? "Banka" : "K.Kartı"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-emerald-600 font-semibold tabular-nums">
                              +{formatCurrency(coll.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-emerald-50">
                          <TableCell colSpan={4} className="font-semibold">Toplam Tahsilat</TableCell>
                          <TableCell className="text-right font-bold text-emerald-700 tabular-nums">
                            +{formatCurrency(cashFlow.summary.inflows)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              )}

              {/* Payments */}
              {cashFlow?.payments?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <ArrowUpCircle className="h-4 w-4 text-red-600" />
                    Ödemeler ({cashFlow.payments.length})
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Tarih</TableHead>
                          <TableHead className="text-xs">Makbuz No</TableHead>
                          <TableHead className="text-xs">Tedarikçi</TableHead>
                          <TableHead className="text-xs">Yöntem</TableHead>
                          <TableHead className="text-xs text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cashFlow.payments.map((pay, idx) => (
                          <TableRow key={pay.id || idx}>
                            <TableCell className="text-sm">{formatDate(pay.date)}</TableCell>
                            <TableCell className="font-mono text-xs">{pay.receipt_no}</TableCell>
                            <TableCell className="text-sm">{pay.supplier_name}</TableCell>
                            <TableCell className="text-sm">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                pay.method === "cash" ? "bg-green-100 text-green-700" :
                                pay.method === "bank" ? "bg-blue-100 text-blue-700" :
                                "bg-purple-100 text-purple-700"
                              }`}>
                                {pay.method === "cash" ? "Nakit" : pay.method === "bank" ? "Banka" : "K.Kartı"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-red-600 font-semibold tabular-nums">
                              -{formatCurrency(pay.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-red-50">
                          <TableCell colSpan={4} className="font-semibold">Toplam Ödeme</TableCell>
                          <TableCell className="text-right font-bold text-red-700 tabular-nums">
                            -{formatCurrency(cashFlow.payments.reduce((sum, p) => sum + p.amount, 0))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              )}

              {!cashFlow?.collections?.length && !cashFlow?.payments?.length && (
                <div className="text-center py-12">
                  <LineChart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Bu dönemde nakit hareketi bulunmuyor</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-lg">Müşteri Bakiye Raporu</CardTitle>
                  <CardDescription>{customerBalance?.customers?.length || 0} müşteri</CardDescription>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-sm text-slate-500">Toplam Alacak</p>
                  <p className="text-2xl font-bold text-emerald-600 tabular-nums">
                    {formatCurrency(customerBalance?.total_receivables)}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {customerBalance?.customers?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Hesap No</TableHead>
                        <TableHead className="text-xs">Müşteri Adı</TableHead>
                        <TableHead className="text-xs hidden md:table-cell">Telefon</TableHead>
                        <TableHead className="text-xs hidden lg:table-cell">E-posta</TableHead>
                        <TableHead className="text-xs text-right">Bakiye</TableHead>
                        <TableHead className="text-xs text-center">Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customerBalance.customers.map((customer, idx) => (
                        <TableRow key={customer.id || idx}>
                          <TableCell className="font-mono text-xs">{customer.account_number || "-"}</TableCell>
                          <TableCell className="font-medium">{customer.name}</TableCell>
                          <TableCell className="text-slate-500 hidden md:table-cell">{customer.phone || "-"}</TableCell>
                          <TableCell className="text-slate-500 hidden lg:table-cell">{customer.email || "-"}</TableCell>
                          <TableCell className={`text-right font-semibold tabular-nums ${
                            customer.balance > 0 ? "text-emerald-600" : customer.balance < 0 ? "text-red-600" : "text-slate-600"
                          }`}>
                            {formatCurrency(customer.balance)}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              customer.balance > 0 ? "bg-emerald-100 text-emerald-700" :
                              customer.balance < 0 ? "bg-red-100 text-red-700" :
                              "bg-slate-100 text-slate-700"
                            }`}>
                              {customer.balance > 0 ? "Alacaklı" : customer.balance < 0 ? "Borçlu" : "Denk"}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Henüz müşteri kaydı yok</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-lg">Tedarikçi Bakiye Raporu</CardTitle>
                  <CardDescription>{supplierBalance?.suppliers?.length || 0} tedarikçi</CardDescription>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-sm text-slate-500">Toplam Borç</p>
                  <p className="text-2xl font-bold text-red-600 tabular-nums">
                    {formatCurrency(supplierBalance?.total_payables)}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {supplierBalance?.suppliers?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Tedarikçi Adı</TableHead>
                        <TableHead className="text-xs hidden md:table-cell">Telefon</TableHead>
                        <TableHead className="text-xs hidden lg:table-cell">E-posta</TableHead>
                        <TableHead className="text-xs text-right">Bakiye</TableHead>
                        <TableHead className="text-xs text-center">Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supplierBalance.suppliers.map((supplier, idx) => (
                        <TableRow key={supplier.id || idx}>
                          <TableCell className="font-medium">{supplier.name}</TableCell>
                          <TableCell className="text-slate-500 hidden md:table-cell">{supplier.phone || "-"}</TableCell>
                          <TableCell className="text-slate-500 hidden lg:table-cell">{supplier.email || "-"}</TableCell>
                          <TableCell className={`text-right font-semibold tabular-nums ${
                            supplier.balance > 0 ? "text-red-600" : supplier.balance < 0 ? "text-emerald-600" : "text-slate-600"
                          }`}>
                            {formatCurrency(supplier.balance)}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              supplier.balance > 0 ? "bg-red-100 text-red-700" :
                              supplier.balance < 0 ? "bg-emerald-100 text-emerald-700" :
                              "bg-slate-100 text-slate-700"
                            }`}>
                              {supplier.balance > 0 ? "Borçlu" : supplier.balance < 0 ? "Alacaklı" : "Denk"}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Truck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Henüz tedarikçi kaydı yok</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cash Accounts */}
            <Card>
              <CardHeader className="pb-2 bg-green-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Banknote className="h-5 w-5 text-green-600" />
                    Kasa Hesapları
                  </CardTitle>
                  <p className="text-lg font-bold text-green-600 tabular-nums">
                    {formatCurrency(debtDetail?.accounts?.cash?.total)}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {debtDetail?.accounts?.cash?.list?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Hesap Adı</TableHead>
                        <TableHead className="text-xs">Para Birimi</TableHead>
                        <TableHead className="text-xs text-right">Bakiye</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {debtDetail.accounts.cash.list.map((acc, idx) => (
                        <TableRow key={acc.id || idx}>
                          <TableCell className="font-medium">{acc.name}</TableCell>
                          <TableCell className="text-slate-500">{acc.currency || "TRY"}</TableCell>
                          <TableCell className="text-right font-semibold text-green-600 tabular-nums">
                            {formatCurrency(acc.balance)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500 text-sm">Kasa hesabı yok</div>
                )}
              </CardContent>
            </Card>

            {/* Bank Accounts */}
            <Card>
              <CardHeader className="pb-2 bg-blue-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    Banka Hesapları
                  </CardTitle>
                  <p className="text-lg font-bold text-blue-600 tabular-nums">
                    {formatCurrency(debtDetail?.accounts?.bank?.total)}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {debtDetail?.accounts?.bank?.list?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Hesap Adı</TableHead>
                        <TableHead className="text-xs">Banka</TableHead>
                        <TableHead className="text-xs text-right">Bakiye</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {debtDetail.accounts.bank.list.map((acc, idx) => (
                        <TableRow key={acc.id || idx}>
                          <TableCell className="font-medium">{acc.name}</TableCell>
                          <TableCell className="text-slate-500">{acc.bank_name || "-"}</TableCell>
                          <TableCell className="text-right font-semibold text-blue-600 tabular-nums">
                            {formatCurrency(acc.balance)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500 text-sm">Banka hesabı yok</div>
                )}
              </CardContent>
            </Card>

            {/* Credit Cards */}
            <Card>
              <CardHeader className="pb-2 bg-purple-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-purple-600" />
                    Kredi Kartları
                  </CardTitle>
                  <p className="text-lg font-bold text-red-600 tabular-nums">
                    -{formatCurrency(debtDetail?.accounts?.credit_cards?.total_debt)}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {debtDetail?.accounts?.credit_cards?.list?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Kart Adı</TableHead>
                        <TableHead className="text-xs">Limit</TableHead>
                        <TableHead className="text-xs text-right">Borç</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {debtDetail.accounts.credit_cards.list.map((card, idx) => (
                        <TableRow key={card.id || idx}>
                          <TableCell className="font-medium">{card.name}</TableCell>
                          <TableCell className="text-slate-500 tabular-nums">{formatCurrency(card.credit_limit)}</TableCell>
                          <TableCell className="text-right font-semibold text-red-600 tabular-nums">
                            -{formatCurrency(card.current_debt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500 text-sm">Kredi kartı yok</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Total Summary */}
          <Card className="bg-slate-50">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">Toplam Kasa</p>
                  <p className="text-2xl font-bold text-green-600 tabular-nums">
                    {formatCurrency(debtDetail?.accounts?.cash?.total)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">Toplam Banka</p>
                  <p className="text-2xl font-bold text-blue-600 tabular-nums">
                    {formatCurrency(debtDetail?.accounts?.bank?.total)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">K.Kartı Borcu</p>
                  <p className="text-2xl font-bold text-red-600 tabular-nums">
                    -{formatCurrency(debtDetail?.accounts?.credit_cards?.total_debt)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">Net Nakit Varlık</p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">
                    {formatCurrency(debtDetail?.summary?.total_liquid_assets)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
